import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { ChatDocumentCard } from '../models/chat-message.model';

export interface FilterChip {
    value: string;
    count: number;
}

export interface FilterGroup {
    label: string;
    field: string;
    chips: FilterChip[];
}

export type SortOrder = 'none' | 'newest' | 'oldest';

@Component({
    selector: 'app-ewo-filter-bar',
    templateUrl: './ewo-filter-bar.component.html',
    styleUrls: ['./ewo-filter-bar.component.scss'],
    standalone: false
})
export class EwoFilterBarComponent implements OnChanges {
    @Input() documents: ChatDocumentCard[] = [];
    /** Emits ordered array of visible doc IDs. Empty array = show all in original order. */
    @Output() filteredIds = new EventEmitter<string[]>();

    public filterGroups: FilterGroup[] = [];
    public activeFilters: Record<string, string | null> = {};
    public sortOrder: SortOrder = 'none';
    public sortDropdownOpen: boolean = false;
    public visibleCount: number = 0;
    public totalCount: number = 0;

    @HostListener('document:click')
    onDocumentClick(): void {
        this.sortDropdownOpen = false;
    }

    private readonly FILTER_FIELDS: Array<{ label: string; field: keyof ChatDocumentCard }> = [
        { label: 'Facility', field: 'facility' },
        { label: 'Discipline', field: 'discipline' },
        { label: 'Category', field: 'category' },
        { label: 'Equipment Type', field: 'equipmentType' },
        { label: 'Status', field: 'approvalStatus' },
    ];

    private buildFilterGroups(): void {
        const ewoCards = (this.documents || []).filter(d => d.documentType === 'EWO');
        this.totalCount = ewoCards.length;

        this.filterGroups = this.FILTER_FIELDS
            .map(({ label, field }) => {
                const countMap = new Map<string, number>();
                for (const card of ewoCards) {
                    const val = ((card[field] as string | undefined) || '').trim();
                    if (val) {
                        countMap.set(val, (countMap.get(val) || 0) + 1);
                    }
                }
                if (countMap.size < 2) return null;

                const chips: FilterChip[] = Array.from(countMap.entries())
                    .map(([value, count]) => ({ value, count }))
                    .sort((a, b) => b.count - a.count);

                return { label, field: field as string, chips };
            })
            .filter(Boolean) as FilterGroup[];
    }

    public toggleChip(field: string, value: string): void {
        this.activeFilters[field] = this.activeFilters[field] === value ? null : value;
        this.emitState();
    }

    public toggleSortDropdown(event: MouseEvent): void {
        event.stopPropagation();
        this.sortDropdownOpen = !this.sortDropdownOpen;
    }

    public selectSort(order: 'newest' | 'oldest'): void {
        // Clicking the already-selected option deselects it (back to none)
        this.sortOrder = this.sortOrder === order ? 'none' : order;
        this.sortDropdownOpen = false;
        this.emitState();
    }

    public clearAll(): void {
        this.activeFilters = {};
        this.sortOrder = 'none';
        this.sortDropdownOpen = false;
        this.visibleCount = this.totalCount;
        this.filteredIds.emit([]);  // empty = default state
    }

    public hasActiveFilters(): boolean {
        return Object.values(this.activeFilters).some(v => !!v);
    }

    public hasAnyActiveState(): boolean {
        return this.hasActiveFilters() || this.sortOrder !== 'none';
    }

    public getSortLabel(): string {
        if (this.sortOrder === 'newest') return 'Newest First';
        if (this.sortOrder === 'oldest') return 'Oldest First';
        return 'Sort by Date';
    }

    public getSortIcon(): string {
        if (this.sortOrder === 'newest') return 'arrow_downward';
        if (this.sortOrder === 'oldest') return 'arrow_upward';
        return 'swap_vert';
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['documents']) {
            this.buildFilterGroups();
            this.activeFilters = {};
            this.sortOrder = 'none';
            this.sortDropdownOpen = false;
            this.visibleCount = this.totalCount;
        }
    }

    private emitState(): void {
        const isDefault = !this.hasActiveFilters() && this.sortOrder === 'none';

        if (isDefault) {
            this.visibleCount = this.totalCount;
            this.filteredIds.emit([]);  // empty = show all in original order
            return;
        }

        let cards = this.getFilteredCards();
        cards = this.applySortOrder(cards);
        this.visibleCount = cards.length;
        this.filteredIds.emit(cards.map(d => d.id));
    }

    private getFilteredCards(): ChatDocumentCard[] {
        const ewoCards = (this.documents || []).filter(d => d.documentType === 'EWO');
        return ewoCards.filter(card =>
            this.filterGroups.every(group => {
                const active = this.activeFilters[group.field];
                if (!active) return true;
                return ((card[group.field as keyof ChatDocumentCard] as string | undefined) || '').trim() === active;
            })
        );
    }

    private applySortOrder(cards: ChatDocumentCard[]): ChatDocumentCard[] {
        if (this.sortOrder === 'none') return cards;

        return [...cards].sort((a, b) => {
            const dateA = this.parseDate(a.date);
            const dateB = this.parseDate(b.date);
            return this.sortOrder === 'newest'
                ? dateB - dateA
                : dateA - dateB;
        });
    }

    private parseDate(dateStr: string | undefined): number {
        if (!dateStr) return 0;
        const parsed = Date.parse(dateStr);
        return isNaN(parsed) ? 0 : parsed;
    }

    public getActiveFilterCount(): number {
        return Object.values(this.activeFilters).filter(v => !!v).length;
    }
}
